import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function SuratKeluar() {
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [bidangId, setBidangId] = useState(null);
  const [suratList, setSuratList] = useState([]);
  const [filteredSuratList, setFilteredSuratList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAkses, setSelectedAkses] = useState(null);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());
  const [selectedBulan, setSelectedBulan] = useState(null);
  const [bidangList, setBidangList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [ditandatangiOlehOptions, setDitandatangiOlehOptions] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limitPerPage, setLimitPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("terbaru"); // terbaru, terlama
  const [viewMode, setViewMode] = useState("grid"); // grid, list
  const [aksesStats, setAksesStats] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2.5 MB in bytes

  // Dialog PDF state
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  // Form state
  const [form, setForm] = useState({
    nomor_surat: "",
    perihal: "",
    kepada: "",
    tanggal: "",
    file_surat: "", // untuk link eksternal
    ditandatangi_oleh: "",
    bidang_id: null,
    akses: "personal",
  });

  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  // Generate tahun list (current year down to 2025)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push({ label: i.toString(), value: i });
  }

  // Akses options
  const aksesOptions = ["publik", "pemda", "instansi", "bidang", "personal"];

  // Static list untuk ditandatangi oleh
  const staticDitandatangiOleh = [
    "Bupati",
    "Wakil Bupati",
    "Sekretaris Daerah",
    "Asisten I",
    "Asisten 2",
    "Asisten 3",
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      const userIdValue = user.uid
        ? String(user.uid)
        : user.user_id
        ? String(user.user_id)
        : null;
      setUserId(userIdValue);
      setBidangId(user.bidang_id);
      if (user.dinas_id && userIdValue) {
        fetchSuratKeluar(user.dinas_id, userIdValue, 1);
        fetchBidangList(user.dinas_id);
        fetchUsersList(user.dinas_id);
      }
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId && userId) {
      setCurrentPage(1);
      fetchSuratKeluar(dinasId, userId, 1);
    }
  }, [
    selectedAkses,
    selectedTahun,
    selectedBulan,
    dinasId,
    userId,
    searchTerm,
    limitPerPage,
    sortBy,
  ]);

  // Fetch bidang list
  const fetchBidangList = async (dinasId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/bidang/${dinasId}`
      );
      setBidangList(response.data || []);
    } catch (error) {
      console.error("Error fetching bidang list:", error);
    }
  };

  // Fetch users list untuk ditandatangi oleh (level 1-3)
  const fetchUsersList = async (dinasId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/dinas/${dinasId}`
      );
      const users = response.data || [];
      // Filter users dengan level_jabatan 1-3 dan status_EO Aktif
      const filteredUsers = users.filter(
        (user) =>
          user.level_jabatan >= 1 &&
          user.level_jabatan <= 3 &&
          user.status_EO === "Aktif"
      );
      setUsersList(filteredUsers);

      // Buat options untuk dropdown ditandatangi_oleh
      const options = [
        ...staticDitandatangiOleh.map((jabatan) => ({
          value: jabatan,
          label: jabatan,
          isStatic: true,
        })),
        ...filteredUsers.map((user) => ({
          value: `${user.nama} - ${user.nama_jabatan || ""}`,
          label: `${user.nama} - ${user.nama_jabatan || ""}`,
          isStatic: false,
          user_id: user.user_id,
        })),
      ];
      setDitandatangiOlehOptions(options);
    } catch (error) {
      console.error("Error fetching users list:", error);
      // Jika error, set minimal dengan static list
      const options = staticDitandatangiOleh.map((jabatan) => ({
        value: jabatan,
        label: jabatan,
        isStatic: true,
      }));
      setDitandatangiOlehOptions(options);
    }
  };

  const fetchSuratKeluar = async (idDinas, idUser, page = 1) => {
    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}surat-keluar?dinas_id=${idDinas}&user_id=${idUser}&page=${page}&limit=${limitPerPage}`;
      if (selectedAkses) {
        url += `&akses=${selectedAkses}`;
      }
      if (selectedTahun) {
        url += `&tahun=${selectedTahun}`;
      }
      if (selectedBulan) {
        url += `&bulan=${selectedBulan}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await axios.get(url);
      let data = response.data.data || [];

      // Sorting berdasarkan sortBy
      if (sortBy === "terlama") {
        data = [...data].sort(
          (a, b) => new Date(a.tanggal) - new Date(b.tanggal)
        );
      }
      // terbaru adalah default (sudah diurutkan dari backend)

      const totalData = response.data.total || 0;
      const totalPagesData = response.data.totalPages || 1;

      setSuratList(data);
      setFilteredSuratList(data);
      setTotal(totalData);
      setTotalPages(totalPagesData);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching surat keluar:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data surat keluar",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // useEffect untuk menghitung statistik per akses
  useEffect(() => {
    if (suratList.length > 0) {
      const stats = {};
      aksesOptions.forEach((akses) => {
        stats[akses] = 0;
      });

      suratList.forEach((surat) => {
        if (surat.akses && stats.hasOwnProperty(surat.akses)) {
          stats[surat.akses] = (stats[surat.akses] || 0) + 1;
        }
      });

      setAksesStats(stats);
    } else {
      setAksesStats({});
    }
  }, [suratList]);

  // Handler untuk perubahan halaman
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && dinasId && userId) {
      fetchSuratKeluar(dinasId, userId, newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format waktu relatif
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "Baru saja";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} menit yang lalu`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} jam yang lalu`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} hari yang lalu`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} bulan yang lalu`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} tahun yang lalu`;
  };

  const getAksesColor = (akses) => {
    const colors = {
      publik: "success",
      pemda: "info",
      instansi: "warning",
      bidang: "primary",
      personal: "secondary",
    };
    return colors[akses] || "default";
  };

  const handleOpenModal = (isEditMode = false, id = null) => {
    setIsEdit(isEditMode);
    setEditingId(id);
    if (isEditMode && id) {
      // Load data untuk edit
      const surat = suratList.find((s) => s.surat_id === id);
      if (surat) {
        const isExternalLink = surat.file_surat?.startsWith("http");
        setForm({
          nomor_surat: surat.nomor_surat || "",
          perihal: surat.perihal || "",
          kepada: surat.kepada || "",
          tanggal: surat.tanggal || "",
          file_surat: isExternalLink ? surat.file_surat : "",
          ditandatangi_oleh: surat.ditandatangi_oleh || "",
          bidang_id: surat.bidang_id || null,
          akses: surat.akses || "personal",
        });
        setSelectedFile(null);
        setFileSizeError(false);
      }
    } else {
      // Reset form untuk tambah baru
      setForm({
        nomor_surat: "",
        perihal: "",
        kepada: "",
        tanggal: "",
        file_surat: "",
        ditandatangi_oleh: "",
        bidang_id: bidangId || null,
        akses: "personal",
      });
      setSelectedFile(null);
      setFileSizeError(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingId(null);
    setForm({
      nomor_surat: "",
      perihal: "",
      kepada: "",
      tanggal: "",
      file_surat: "",
      ditandatangi_oleh: "",
      bidang_id: bidangId || null,
      akses: "personal",
    });
    setSelectedFile(null);
    setFileSizeError(false);
  };

  const handleEdit = (id) => {
    handleOpenModal(true, id);
  };

  // Handler untuk file change (hanya PDF)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    // Validasi tipe file (hanya PDF)
    if (file.type !== "application/pdf") {
      swal({
        title: "Format File Tidak Valid!",
        text: "Hanya file PDF yang diizinkan untuk surat keluar.",
        icon: "error",
        button: "OK",
      });
      e.target.value = "";
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    // Validasi ukuran file (maksimal 2.5 MB)
    if (file.size > MAX_FILE_SIZE) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: `Ukuran file (${(file.size / 1024 / 1024).toFixed(
          2
        )} MB) melebihi batas maksimal 2.5 MB. Silakan gunakan link Google Drive untuk file yang lebih besar.`,
        icon: "error",
        button: "OK",
      });
      e.target.value = "";
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    // File valid
    setFileSizeError(false);
    setSelectedFile(file);
    setForm((prev) => ({ ...prev, file_surat: "" }));
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileSizeError(false);
    const fileInput = document.getElementById("file-upload-surat-keluar");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleLinkChange = (e) => {
    const linkValue = e.target.value;
    setForm((prev) => ({ ...prev, file_surat: linkValue }));
    if (linkValue && linkValue.trim() !== "") {
      setSelectedFile(null);
      setFileSizeError(false);
    }
  };

  const handleSubmit = async () => {
    // Validasi field wajib
    if (
      !form.nomor_surat ||
      !form.perihal ||
      !form.kepada ||
      !form.tanggal ||
      !form.ditandatangi_oleh ||
      !form.akses
    ) {
      swal({
        title: "Error",
        text: "Field nomor surat, perihal, kepada, tanggal, ditandatangi oleh, dan akses wajib diisi",
        icon: "error",
      });
      return;
    }

    // Validasi file atau link
    const hasValidFile =
      selectedFile && selectedFile instanceof File && selectedFile.size > 0;
    const hasLink = form.file_surat && form.file_surat.trim() !== "";

    if (!hasValidFile && !hasLink) {
      swal({
        title: "Error",
        text: "File surat wajib diisi. Pilih salah satu: upload file PDF (maks 2.5 MB) atau masukkan link eksternal (Google Drive, dll)",
        icon: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("nomor_surat", form.nomor_surat);
      formData.append("perihal", form.perihal);
      formData.append("kepada", form.kepada);
      formData.append("tanggal", form.tanggal);
      formData.append("ditandatangi_oleh", form.ditandatangi_oleh);
      formData.append("dinas_id", dinasId);
      formData.append("akses", form.akses);
      const userIdInt = userId ? parseInt(userId) : null;
      if (!userIdInt || isNaN(userIdInt)) {
        swal({
          title: "Error",
          text: "User ID tidak valid. Silakan login kembali.",
          icon: "error",
        });
        setSubmitting(false);
        return;
      }
      formData.append("user_id", userIdInt);
      if (form.bidang_id) {
        formData.append("bidang_id", form.bidang_id);
      }

      // Pastikan hanya salah satu yang dikirim
      if (hasValidFile && selectedFile) {
        formData.append("file", selectedFile);
      } else if (hasLink && form.file_surat && form.file_surat.trim() !== "") {
        formData.append("file_surat", form.file_surat.trim());
      }

      let response;
      if (isEdit) {
        response = await axios.put(
          `${API_ESA_OFFICE}surat-keluar/${editingId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        response = await axios.post(`${API_ESA_OFFICE}surat-keluar`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success) {
        swal({
          title: "Berhasil",
          text: isEdit
            ? "Surat keluar berhasil diupdate"
            : "Surat keluar berhasil ditambahkan",
          icon: "success",
        });
        handleCloseModal();
        fetchSuratKeluar(dinasId, userId, currentPage);
      } else {
        throw new Error(
          response.data.message || "Gagal menyimpan surat keluar"
        );
      }
    } catch (error) {
      console.error("Error submitting surat keluar:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        (isEdit
          ? "Gagal mengupdate surat keluar"
          : "Gagal menambahkan surat keluar");

      swal({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, nomorSurat) => {
    const confirm = await swal({
      title: "Hapus Surat Keluar?",
      text: `Apakah Anda yakin ingin menghapus surat keluar "${nomorSurat}"?`,
      icon: "warning",
      buttons: {
        cancel: "Batal",
        confirm: {
          text: "Hapus",
          value: true,
        },
      },
      dangerMode: true,
    });

    if (confirm) {
      try {
        await axios.delete(`${API_ESA_OFFICE}surat-keluar/${id}`);
        swal({
          title: "Berhasil",
          text: "Surat keluar berhasil dihapus",
          icon: "success",
        });
        fetchSuratKeluar(dinasId, userId, currentPage);
      } catch (error) {
        console.error("Error deleting surat keluar:", error);
        swal({
          title: "Error",
          text: "Gagal menghapus surat keluar",
          icon: "error",
        });
      }
    }
  };

  // Handler untuk membuka file
  const handleFileClick = (surat) => {
    if (!surat.file_surat) return;

    if (surat.file_surat.startsWith("http")) {
      // Link eksternal - buka di tab baru
      window.open(surat.file_surat, "_blank", "noopener,noreferrer");
      return;
    } else {
      // File internal - backend sudah mengembalikan path lengkap /uploads/suratkeluar/...
      const fileUrl = `${API_ESA_OFFICE_UPLOAD}${surat.file_surat}`;
      // PDF selalu dibuka di dialog
      setSelectedFileUrl(fileUrl);
      setOpenFileDialog(true);
    }
  };

  // Handler untuk menutup dialog PDF
  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFileUrl(null);
  };

  const getFileIcon = (surat) => {
    if (!surat.file_surat)
      return <Icon fontSize="small">insert_drive_file</Icon>;

    if (surat.file_surat.startsWith("http")) {
      return <Icon fontSize="small">link</Icon>;
    } else {
      return <Icon fontSize="small">picture_as_pdf</Icon>;
    }
  };

  const getFileButtonLabel = (surat) => {
    if (!surat.file_surat) return "Tidak ada file";

    if (surat.file_surat.startsWith("http")) {
      return "Buka Link";
    } else {
      return "Buka PDF";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white" mb={0}>
                    Surat Keluar
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="white"
                    size="small"
                    onClick={() => handleOpenModal(false)}
                  >
                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                      add
                    </Icon>
                    Tambah Surat Keluar
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                >
                  <MDInput
                    label="Cari (Nomor, Perihal, Kepada, atau Ditandatangi Oleh)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      flexGrow: 1,
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    options={tahunList}
                    value={
                      selectedTahun
                        ? {
                            label: selectedTahun.toString(),
                            value: selectedTahun,
                          }
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTahun(newValue.value);
                      } else {
                        setSelectedTahun(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Tahun"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 120,
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={bulanList}
                    value={
                      selectedBulan
                        ? bulanList.find((b) => b.value === selectedBulan) ||
                          null
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedBulan(newValue.value);
                      } else {
                        setSelectedBulan(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Bulan"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={aksesOptions}
                    value={selectedAkses}
                    onChange={(event, newValue) => {
                      setSelectedAkses(newValue);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Akses"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                    getOptionLabel={(option) =>
                      option.replace(/\b\w/g, (l) => l.toUpperCase())
                    }
                  />
                </MDBox>

                {/* Badge Informasi Total dan Per Akses */}
                {!loading && total > 0 && (
                  <MDBox
                    mx={2}
                    mb={2}
                    pt={2}
                    pb={2}
                    sx={{
                      borderTop: "1px solid #e0e0e0",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <MDBox mb={2}>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color="text"
                        mb={1}
                      >
                        Informasi Surat Keluar
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexWrap="wrap"
                      gap={1.5}
                      alignItems="center"
                    >
                      <Chip
                        icon={<Icon sx={{ fontSize: 18 }}>send</Icon>}
                        label={`Total: ${total} Surat Keluar`}
                        color="success"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: "medium",
                          height: "32px",
                        }}
                      />
                      {aksesOptions.map((akses) => {
                        const count = aksesStats[akses] || 0;
                        if (count === 0) return null;
                        const chipColor = getAksesColor(akses) || "default";
                        return (
                          <Chip
                            key={akses}
                            label={`${akses.replace(/\b\w/g, (l) =>
                              l.toUpperCase()
                            )}: ${count}`}
                            color={chipColor}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                        );
                      })}
                    </MDBox>
                  </MDBox>
                )}

                {/* Informasi dan Kontrol di Bagian Atas */}
                {!loading && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                    pt={2}
                  >
                    {/* Kiri: Dropdown Limit dan Sorting */}
                    <MDBox display="flex" gap={3} alignItems="flex-start">
                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Per Halaman
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={limitPerPage}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setLimitPerPage(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={[9, 10, 20, 50]}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>

                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Urutkan
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={sortBy}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setSortBy(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={["terbaru", "terlama"]}
                          getOptionLabel={(option) => {
                            const labels = {
                              terbaru: "Terbaru",
                              terlama: "Terlama",
                            };
                            return labels[option] || option;
                          }}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>
                    </MDBox>

                    {/* Kanan: Toggle View */}
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(e, newView) => {
                        if (newView !== null) setViewMode(newView);
                      }}
                      size="large"
                    >
                      <ToggleButton value="grid" aria-label="grid view">
                        <Icon sx={{ fontSize: 28 }}>grid_view</Icon>
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="list view">
                        <Icon sx={{ fontSize: 28 }}>view_list</Icon>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </MDBox>
                )}

                {/* Info Dataset dan Pagination */}
                {!loading && total > 0 && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                  >
                    <MDTypography variant="body2" color="text">
                      Menampilkan {(currentPage - 1) * limitPerPage + 1} sampai{" "}
                      {Math.min(currentPage * limitPerPage, total)} dari {total}{" "}
                      surat keluar
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Halaman {currentPage} dari {totalPages}
                    </MDTypography>
                  </MDBox>
                )}

                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox px={3}>
                    {filteredSuratList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data surat keluar
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchTerm
                            ? "Tidak ada data yang sesuai dengan pencarian"
                            : "Belum ada surat keluar yang terdaftar"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                        {viewMode === "grid" ? (
                          // Tampilan Grid
                          <Grid container spacing={2}>
                            {filteredSuratList.map((surat) => {
                              const fotoUrl = surat.foto_pengupload || null;
                              const isUploader =
                                userId &&
                                surat.id_pengupload &&
                                String(userId) === String(surat.id_pengupload);

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={surat.surat_id}
                                >
                                  <Card
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      borderRadius: 2,
                                      boxShadow: 2,
                                      backgroundColor: "#fff",
                                      transition: "box-shadow 0.3s",
                                      "&:hover": {
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    <MDBox
                                      p={2.5}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                      }}
                                    >
                                      {/* Tags di bagian atas */}
                                      <MDBox
                                        display="flex"
                                        gap={1}
                                        alignItems="center"
                                        mb={1.5}
                                        flexWrap="wrap"
                                      >
                                        <Chip
                                          label={
                                            surat.akses?.replace(/\b\w/g, (l) =>
                                              l.toUpperCase()
                                            ) || "-"
                                          }
                                          size="small"
                                          sx={{
                                            backgroundColor: "#4caf50",
                                            color: "#fff",
                                            fontWeight: "medium",
                                            fontSize: "0.75rem",
                                          }}
                                        />
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox
                                        flex={1}
                                        display="flex"
                                        flexDirection="column"
                                      >
                                        {/* Nomor Surat */}
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                          color="text"
                                          mb={1}
                                          sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            minHeight: "3rem",
                                          }}
                                        >
                                          {surat.nomor_surat || "-"}
                                        </MDTypography>

                                        {/* Kepada */}
                                        <MDBox mb={1}>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="medium"
                                          >
                                            Kepada:
                                          </MDTypography>
                                          <MDTypography
                                            variant="body2"
                                            color="text"
                                            sx={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 1,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                            }}
                                          >
                                            {surat.kepada || "-"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Ditandatangi Oleh */}
                                        <MDBox mb={1}>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="medium"
                                          >
                                            Ditandatangi Oleh:
                                          </MDTypography>
                                          <MDTypography
                                            variant="body2"
                                            color="text"
                                            sx={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 1,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                            }}
                                          >
                                            {surat.ditandatangi_oleh || "-"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Perihal */}
                                        {surat.perihal && (
                                          <MDBox mb={1.5} flex={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                              fontWeight="medium"
                                            >
                                              Perihal:
                                            </MDTypography>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                minHeight: "4.8rem",
                                              }}
                                            >
                                              {surat.perihal}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1.5}
                                        >
                                          {fotoUrl ? (
                                            <Avatar
                                              src={fotoUrl}
                                              alt={surat.nama_user || "User"}
                                              sx={{ width: 20, height: 20 }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 12 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {surat.nama_user ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>
                                      </MDBox>

                                      {/* Footer: Timestamp dan Action buttons */}
                                      <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mt="auto"
                                        pt={1}
                                        sx={{
                                          borderTop: "1px solid #e0e0e0",
                                        }}
                                      >
                                        {/* Timestamp */}
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatRelativeTime(surat.tanggal)}
                                        </MDTypography>

                                        {/* Action buttons */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                          flexWrap="wrap"
                                        >
                                          {/* Icon Download/Buka File */}
                                          {surat.file_surat && (
                                            <IconButton
                                              size="small"
                                              color="info"
                                              onClick={() =>
                                                handleFileClick(surat)
                                              }
                                              title={getFileButtonLabel(surat)}
                                            >
                                              {getFileIcon(surat)}
                                            </IconButton>
                                          )}
                                          {/* Icon Edit dan Hapus - hanya tampil jika user adalah pengupload */}
                                          {isUploader && (
                                            <>
                                              <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() =>
                                                  handleEdit(surat.surat_id)
                                                }
                                                title="Edit"
                                              >
                                                <Icon fontSize="small">
                                                  edit
                                                </Icon>
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  handleDelete(
                                                    surat.surat_id,
                                                    surat.nomor_surat
                                                  )
                                                }
                                                title="Hapus"
                                              >
                                                <Icon fontSize="small">
                                                  delete
                                                </Icon>
                                              </IconButton>
                                            </>
                                          )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : (
                          // Tampilan List
                          <>
                            {filteredSuratList.map((surat) => {
                              const fotoUrl = surat.foto_pengupload || null;
                              const isUploader =
                                userId &&
                                surat.id_pengupload &&
                                String(userId) === String(surat.id_pengupload);

                              return (
                                <Card
                                  key={surat.surat_id}
                                  sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <MDBox p={2.5}>
                                    <MDBox display="flex" gap={2}>
                                      {/* Icon di kiri */}
                                      <MDBox
                                        sx={{
                                          minWidth: 60,
                                          height: 60,
                                          borderRadius: "50%",
                                          backgroundColor: "#4caf50",
                                          border: "2px solid #2e7d32",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 30,
                                            color: "#fff",
                                          }}
                                        >
                                          send
                                        </Icon>
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox flex={1}>
                                        {/* Header dengan nomor surat dan timestamp */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="start"
                                          mb={1}
                                        >
                                          <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="text"
                                            sx={{ flex: 1 }}
                                          >
                                            {surat.nomor_surat || "-"}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ ml: 2 }}
                                          >
                                            {formatRelativeTime(surat.tanggal)}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Akses Badge */}
                                        <MDBox mb={1}>
                                          <Chip
                                            label={
                                              surat.akses?.replace(
                                                /\b\w/g,
                                                (l) => l.toUpperCase()
                                              ) || "-"
                                            }
                                            size="small"
                                            sx={{
                                              backgroundColor: "#4caf50",
                                              color: "#fff",
                                              fontWeight: "medium",
                                            }}
                                          />
                                        </MDBox>

                                        {/* Kepada */}
                                        <MDBox mb={1}>
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Icon
                                              sx={{
                                                fontSize: 16,
                                                color: "text.secondary",
                                              }}
                                            >
                                              business
                                            </Icon>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Kepada: {surat.kepada || "-"}
                                            </MDTypography>
                                          </MDBox>
                                        </MDBox>

                                        {/* Ditandatangi Oleh */}
                                        <MDBox mb={1}>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Ditandatangi Oleh:{" "}
                                            {surat.ditandatangi_oleh || "-"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1}
                                        >
                                          {fotoUrl ? (
                                            <Avatar
                                              src={fotoUrl}
                                              alt={surat.nama_user || "User"}
                                              sx={{ width: 24, height: 24 }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 14 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {surat.nama_user ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Perihal */}
                                        {surat.perihal && (
                                          <MDBox mb={1.5}>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {surat.perihal}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Action buttons - di kanan bawah */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="flex-end"
                                          alignItems="center"
                                          gap={0.5}
                                          mt={1.5}
                                          flexWrap="wrap"
                                        >
                                          {/* Tombol Download/Buka File */}
                                          {surat.file_surat && (
                                            <Button
                                              variant="outlined"
                                              color="info"
                                              size="small"
                                              onClick={() =>
                                                handleFileClick(surat)
                                              }
                                              startIcon={getFileIcon(surat)}
                                              sx={{
                                                textTransform: "none",
                                                minWidth: "auto",
                                              }}
                                            >
                                              {getFileButtonLabel(surat)}
                                            </Button>
                                          )}
                                          {/* Tombol Edit dan Hapus - hanya tampil jika user adalah pengupload */}
                                          {isUploader && (
                                            <>
                                              <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() =>
                                                  handleEdit(surat.surat_id)
                                                }
                                                title="Edit"
                                              >
                                                <Icon fontSize="small">
                                                  edit
                                                </Icon>
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  handleDelete(
                                                    surat.surat_id,
                                                    surat.nomor_surat
                                                  )
                                                }
                                                title="Hapus"
                                              >
                                                <Icon fontSize="small">
                                                  delete
                                                </Icon>
                                              </IconButton>
                                            </>
                                          )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                </Card>
                              );
                            })}
                          </>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            mt={3}
                            mb={2}
                          >
                            <MDPagination variant="gradient" color="success">
                              {currentPage > 1 && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_left
                                  </Icon>
                                </MDPagination>
                              )}

                              {/* Tampilkan beberapa halaman */}
                              {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <MDPagination
                                      key={pageNum}
                                      item
                                      onClick={() => handlePageChange(pageNum)}
                                      active={currentPage === pageNum}
                                    >
                                      {pageNum}
                                    </MDPagination>
                                  );
                                }
                              )}

                              {totalPages > 5 &&
                                currentPage < totalPages - 2 && (
                                  <>
                                    <MDPagination item disabled>
                                      ...
                                    </MDPagination>
                                    <MDPagination
                                      item
                                      onClick={() =>
                                        handlePageChange(totalPages)
                                      }
                                    >
                                      {totalPages}
                                    </MDPagination>
                                  </>
                                )}

                              {currentPage < totalPages && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_right
                                  </Icon>
                                </MDPagination>
                              )}
                            </MDPagination>

                            {/* Input untuk lompat ke halaman */}
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={2}
                            >
                              <MDTypography variant="caption" color="text">
                                Lompat ke halaman:
                              </MDTypography>
                              <MDInput
                                type="number"
                                value={currentPage}
                                onChange={(e) => {
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= totalPages) {
                                    handlePageChange(page);
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  max: totalPages,
                                  style: {
                                    width: "60px",
                                    textAlign: "center",
                                  },
                                }}
                                sx={{ width: "80px" }}
                              />
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handlePageChange(currentPage)}
                                sx={{ minWidth: "auto", px: 2 }}
                              >
                                Go
                              </Button>
                            </MDBox>
                          </MDBox>
                        )}
                      </>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal Form Tambah/Edit Surat Keluar */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h5">
            {isEdit ? "Edit Surat Keluar" : "Tambah Surat Keluar"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Nomor Surat *"
                  value={form.nomor_surat}
                  onChange={(e) =>
                    setForm({ ...form, nomor_surat: e.target.value })
                  }
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Tanggal *"
                  type="date"
                  value={form.tanggal}
                  onChange={(e) =>
                    setForm({ ...form, tanggal: e.target.value })
                  }
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Kepada *"
                  value={form.kepada}
                  onChange={(e) => setForm({ ...form, kepada: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Perihal *"
                  value={form.perihal}
                  onChange={(e) =>
                    setForm({ ...form, perihal: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={ditandatangiOlehOptions}
                  value={
                    form.ditandatangi_oleh
                      ? ditandatangiOlehOptions.find(
                          (opt) => opt.value === form.ditandatangi_oleh
                        ) || null
                      : null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      ditandatangi_oleh: newValue ? newValue.value : "",
                    });
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Ditandatangi Oleh *"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  getOptionLabel={(option) =>
                    typeof option === "object" ? option.label : String(option)
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={bidangList}
                  value={
                    form.bidang_id && bidangList.length > 0
                      ? bidangList.find(
                          (b) => Number(b.bidang_id) === Number(form.bidang_id)
                        ) || null
                      : null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      bidang_id: newValue ? Number(newValue.bidang_id) : null,
                    });
                  }}
                  getOptionLabel={(option) => option.nama_bidang || ""}
                  isOptionEqualToValue={(option, value) =>
                    Number(option.bidang_id) === Number(value?.bidang_id)
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Bidang (Opsional)"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={aksesOptions}
                  value={form.akses || null}
                  onChange={(event, newValue) => {
                    setForm({ ...form, akses: newValue || "personal" });
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Akses *"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  getOptionLabel={(option) =>
                    option.replace(/\b\w/g, (l) => l.toUpperCase())
                  }
                  disableClearable={false}
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  display="block"
                  mb={1}
                >
                  Upload File Surat (PDF, Maksimal 2.5 MB)
                </MDTypography>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-upload-surat-keluar"
                  disabled={!!form.file_surat}
                />
                <label htmlFor="file-upload-surat-keluar">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    disabled={!!form.file_surat}
                    sx={{
                      color: form.file_surat ? "#ccc" : "#000000",
                      borderColor: form.file_surat ? "#ccc" : "#000000",
                    }}
                  >
                    {form.file_surat
                      ? "Upload File (tidak aktif - link sudah diisi)"
                      : "Pilih File PDF"}
                  </Button>
                </label>
                {selectedFile && !fileSizeError && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDBox flex={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          File terpilih: {selectedFile.name}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                        >
                          Ukuran: {(selectedFile.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </MDTypography>
                      </MDBox>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={handleRemoveFile}
                        sx={{ ml: 1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </MDBox>
                  </MDBox>
                )}
                {fileSizeError && (
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="error">
                      ⚠️ File melebihi 2.5 MB. Silakan gunakan link Google Drive
                      untuk file yang lebih besar.
                    </MDTypography>
                  </MDBox>
                )}
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label={
                    selectedFile
                      ? "Link Eksternal (tidak aktif - file sudah diupload)"
                      : "Link Eksternal (Google Drive, dll)"
                  }
                  value={form.file_surat}
                  onChange={handleLinkChange}
                  fullWidth
                  variant="outlined"
                  placeholder="https://drive.google.com/..."
                  disabled={!!selectedFile}
                  helperText={
                    selectedFile
                      ? "Hapus file terlebih dahulu untuk menggunakan link"
                      : "Gunakan link Google Drive untuk file lebih dari 2.5 MB"
                  }
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseModal}>
            Batal
          </MDButton>
          <MDButton
            color="success"
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting ||
              fileSizeError ||
              (selectedFile && selectedFile.size > MAX_FILE_SIZE)
            }
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Menyimpan..." : isEdit ? "Update" : "Simpan"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Surat Keluar</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFileUrl ? (
              <iframe
                src={selectedFileUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SuratKeluar;
